import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import PageWrapper from "@/components/PageWrapper";
import Particles from "@/components/Particles";
import MagneticButton from "@/components/MagneticButton";
import AnimatedCounter from "@/components/AnimatedCounter";
import SectionHeading from "@/components/SectionHeading";
import PortfolioCard from "@/components/PortfolioCard";
import ProjectModal from "@/components/ProjectModal";
import { useState } from "react";
import { SERVICES, PROCESS_STEPS, TESTIMONIALS } from "@/data/services";
import { FEATURED_PROJECTS } from "@/data/projects";

const STATS = [
  { value: 100, suffix: "+", label: "Projects Delivered" },
  { value: 15, suffix: "+", label: "Industries Served" },
  { value: 10, suffix: "+", label: "Countries Reached" },
  { value: 98, suffix: "%", label: "Client Satisfaction" },
];

export default function Home() {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  return (
    <PageWrapper>
      {/* HERO */}
      <section
        data-testid="home-hero-section"
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 gradient-mesh" aria-hidden />
        <Particles count={40} />
        <div className="absolute inset-0 noise-overlay" aria-hidden />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-32 pb-24 md:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md px-4 py-1.5 text-xs text-zinc-300"
          >
            <Sparkles size={14} className="text-blue-400" />
            <span>A premium digital agency · codingsolutions.in</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 font-display text-5xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight text-white max-w-5xl"
          >
            We Build Websites <br className="hidden md:block" />
            That{" "}
            <span className="text-gradient-accent">Grow Businesses</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-7 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed"
          >
            We design and develop high-performance websites, Shopify stores,
            WordPress solutions, and custom web applications that help businesses
            attract customers, generate leads, and increase revenue.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid="hero-cta-primary"
              className="rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm md:text-base font-medium px-7 py-4 shadow-[0_10px_40px_-10px_rgba(59,130,246,0.8)]"
            >
              Start Your Project <ArrowRight size={16} />
            </MagneticButton>
            <MagneticButton
              as={Link}
              to="/portfolio"
              data-testid="hero-cta-secondary"
              strength={10}
              className="rounded-full border border-white/15 hover:border-white/40 text-white text-sm md:text-base font-medium px-7 py-4"
            >
              View Portfolio
            </MagneticButton>
          </motion.div>

          {/* Floating glass stat cards */}
          <div className="relative mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.45 + i * 0.08 }}
                className="glass rounded-2xl p-5 md:p-7"
              >
                <div className="font-display text-3xl md:text-5xl tracking-tight text-white">
                  <AnimatedCounter
                    value={s.value}
                    suffix={s.suffix}
                    data-testid={`hero-stat-${i}`}
                  />
                </div>
                <p className="mt-2 text-xs md:text-sm text-zinc-400">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="relative py-10 border-y border-white/10 bg-[#070707] overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap gap-12 text-zinc-500">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 shrink-0">
              {[
                "Joyfill",
                "Thomsen",
                "SoulBaby",
                "The Luxe Attire",
                "Drink Shift",
                "Wedding Asia",
                "Tailor Junction",
                "Playtics",
                "ARN Holidays",
                "Metal Eye",
                "Puloka",
              ].map((b) => (
                <span
                  key={`${k}-${b}`}
                  className="font-display text-2xl md:text-3xl tracking-tight"
                >
                  {b}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section
        data-testid="home-services-section"
        className="relative py-24 md:py-32 lg:py-40"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <SectionHeading
            eyebrow="What we do"
            title="Premium digital craft, end-to-end."
            description="From Shopify storefronts to custom web platforms — we partner with ambitious brands to build digital products that drive measurable growth."
          />
          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.article
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-7 md:p-9 hover:border-blue-500/40 transition-colors"
                >
                  <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="w-12 h-12 grid place-items-center rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400">
                      <Icon size={20} />
                    </div>
                    <h3 className="mt-6 font-display text-2xl md:text-3xl tracking-tight text-white">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-zinc-400 leading-relaxed">{s.short}</p>
                    <ul className="mt-6 space-y-2 text-sm text-zinc-300">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-blue-400" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="relative py-24 md:py-32 lg:py-40 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="About Coding Solutions"
              title="More Than Just Website Developers."
              description="At Coding Solutions, we combine strategy, design, and development to create digital experiences that deliver measurable business results. From startups to global brands, we help businesses establish a powerful online presence."
            />
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { v: 100, s: "+", l: "Projects" },
                { v: 15, s: "+", l: "Industries" },
                { v: 10, s: "+", l: "Countries" },
              ].map((s, i) => (
                <div key={s.l} className="border-l border-white/10 pl-4">
                  <div className="font-display text-3xl md:text-4xl text-white">
                    <AnimatedCounter
                      value={s.v}
                      suffix={s.s}
                      data-testid={`about-preview-stat-${i}`}
                    />
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="about-preview-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white text-sm px-5 py-3 transition-colors"
            >
              Learn more about us <ArrowRight size={14} />
            </Link>
          </div>

          <div className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Coding Solutions — developer crafting digital experiences"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-blue-300">
                    The Studio
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-white mt-1">
                    Strategy. Design. Engineering.
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PORTFOLIO */}
      <section
        data-testid="home-portfolio-section"
        className="relative py-24 md:py-32 lg:py-40 border-t border-white/10"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <SectionHeading
              eyebrow="Selected work"
              title="A taste of what we ship."
              description="Performance-driven digital products built for global brands — from luxury commerce to sports analytics."
            />
            <Link
              to="/portfolio"
              data-testid="home-portfolio-view-all"
              className="inline-flex items-center gap-2 self-start md:self-end rounded-full border border-white/15 hover:border-white/40 text-white text-sm px-5 py-3 transition-colors"
            >
              View All Projects <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {FEATURED_PROJECTS.slice(0, 6).map((p, i) => (
              <PortfolioCard
                key={p.id}
                project={p}
                index={i}
                onOpen={(proj) => {
                  setActive(proj);
                  setOpen(true);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="relative py-24 md:py-32 lg:py-40 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <SectionHeading
            eyebrow="The process"
            title="A calm, deliberate path to launch."
            description="A proven framework refined across 100+ projects — from first conversation to scaling your platform."
          />
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
              <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-square">
                <div className="absolute inset-0 gradient-mesh" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.25em] text-blue-300">
                      Built with
                    </p>
                    <p className="font-display text-3xl md:text-5xl text-white mt-3">
                      Strategy first.
                    </p>
                    <p className="text-zinc-400 mt-3 max-w-xs mx-auto px-4">
                      We design what works. Beautifully.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ol className="relative border-l border-white/10 pl-8 space-y-12">
                {PROCESS_STEPS.map((step, i) => (
                  <motion.li
                    key={step.label}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, delay: i * 0.05 }}
                    className="relative"
                    data-testid={`process-step-${i}`}
                  >
                    <span className="absolute -left-[37px] top-1 w-4 h-4 rounded-full bg-[#050505] border-2 border-blue-500 shadow-[0_0_0_4px_rgba(59,130,246,0.12)]" />
                    <div className="text-xs uppercase tracking-[0.25em] text-blue-400">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-white mt-2 tracking-tight">
                      {step.label}
                    </h3>
                    <p className="mt-3 text-zinc-400 leading-relaxed max-w-xl">
                      {step.body}
                    </p>
                  </motion.li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24 md:py-32 lg:py-40 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <SectionHeading
            eyebrow="Kind words"
            title="Trusted by founders, marketers and operators."
          />
          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.author}
                data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-8 md:p-10"
              >
                <div className="flex items-center gap-1 text-blue-400">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} size={14} fill="currentColor" stroke="none" />
                  ))}
                </div>
                <blockquote className="mt-5 font-display text-xl md:text-2xl tracking-tight text-white leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 text-sm text-zinc-400">
                  <span className="text-white font-medium">{t.author}</span>
                  <span className="mx-2 text-zinc-600">·</span>
                  {t.title}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 lg:py-40 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-90" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-6 md:px-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight text-white leading-[1.05]"
          >
            Ready To Build Something <span className="text-gradient-accent">Exceptional?</span>
          </motion.h2>
          <p className="mt-6 text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
            Tell us about your project. We&apos;ll come back within 24 hours
            with ideas, references and a clear next step.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid="bottom-cta-primary"
              className="rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm md:text-base font-medium px-7 py-4 shadow-[0_10px_40px_-10px_rgba(59,130,246,0.8)]"
            >
              Book Free Consultation <ArrowRight size={16} />
            </MagneticButton>
          </div>
        </div>
      </section>

      <ProjectModal project={active} open={open} onOpenChange={setOpen} />
    </PageWrapper>
  );
}
